/**
 * Web Push subscription helper for OncoCadence.
 *
 * Flow:
 *  1) `pushSupported()` — checks Notification + ServiceWorker + PushManager are present.
 *  2) `getPushStatus()` — current permission + whether a subscription exists.
 *  3) `subscribeToPush()` — requests permission, fetches VAPID key, subscribes, posts to backend.
 *  4) `unsubscribeFromPush()` — drops local subscription and tells backend.
 *  5) `sendTestPush()` — asks backend to push to all my devices.
 *
 * Defensive: every call is wrapped to never throw across module boundaries — callers
 * receive a `{ ok, reason }` shape so the UI can show a useful message.
 */
import { api } from "@/lib/api";

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const out = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) out[i] = rawData.charCodeAt(i);
  return out;
}

async function getRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  // Wait for the ready SW (registered by registerSW.js on app boot).
  const reg = await navigator.serviceWorker.ready;
  return reg;
}

export async function getPushStatus() {
  if (!pushSupported()) return { supported: false, permission: "default", subscribed: false };
  const reg = await getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return {
    supported:  true,
    permission: Notification.permission,
    subscribed: !!sub,
  };
}

export async function subscribeToPush() {
  if (!pushSupported()) return { ok: false, reason: "Push notifications are not supported in this browser." };

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { ok: false, reason: "Permission denied. Enable notifications in your browser settings." };
  }

  const { data } = await api.get("/push/vapid-public-key");
  const vapid = data?.public_key;
  if (!vapid) return { ok: false, reason: "Server is not configured for web push." };

  const reg = await getRegistration();
  if (!reg) return { ok: false, reason: "Service worker not ready." };

  // Reuse an existing subscription if browser already has one (avoids duplicate keys
  // because the applicationServerKey changing would force a new subscription).
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid),
    });
  }

  await api.post("/push/register", {
    token:       JSON.stringify(sub),
    platform:    "web",
    app_version: "web-1.0",
    locale:      navigator.language || "en-US",
  });

  return { ok: true };
}

export async function unsubscribeFromPush() {
  if (!pushSupported()) return { ok: false, reason: "Not supported" };
  const reg = await getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (sub) {
    try { await api.post("/push/unregister", { token: JSON.stringify(sub) }); } catch (_) { /* ignore — best-effort */ }
    await sub.unsubscribe();
  }
  return { ok: true };
}

export async function sendTestPush() {
  const { data } = await api.post("/push/test");
  return data;
}
